import React, { useState } from 'react';
import { Trash2, ChevronDown } from 'lucide-react';

const DeleteAllModal = ({ 
    show, 
    onClose, 
    onConfirm, 
    candidatesLength, 
    isDeleting,
    onDeleteRejected,
    isDeletingRejected 
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);

    if (!show) return null;

    const handleDeleteAll = () => {
        setSelectedAction('all');
        setShowDropdown(false);
    };

    const handleDeleteRejected = () => {
        setSelectedAction('rejected');
        setShowDropdown(false);
    };

    const handleConfirm = () => {
        if (selectedAction === 'all') {
            onConfirm();
        } else if (selectedAction === 'rejected') {
            onDeleteRejected();
        }
    };

    const getConfirmationText = () => {
        if (selectedAction === 'all') {
            return {
                title: `Delete All ${candidatesLength} Candidates`,
                description: "This will permanently remove all candidate data including their profiles, resumes, and interview records from the entire database."
            };
        } else if (selectedAction === 'rejected') {
            return {
                title: "Delete All Rejected Candidates",
                description: "This will permanently remove all rejected candidates and their related data from the database."
            };
        } else {
            return {
                title: "Choose Delete Action",
                description: "Select which candidates you want to delete from the database."
            };
        }
    };

    const { title, description } = getConfirmationText();
    const isProcessing = isDeleting || isDeletingRejected;

    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50" 
            onClick={onClose}
        >
            <div 
                className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl transform transition-all" 
                onClick={(e) => e.stopPropagation()}
            >
                <div className="text-center">
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
                        <p className="text-gray-500 text-sm">
                            {description}
                        </p>
                    </div>

                    {!selectedAction ? (
                        // Action Selection View
                        <div className="space-y-3 mb-6">
                            <button
                                onClick={handleDeleteAll}
                                className="w-full flex items-center justify-between px-4 py-3 text-left border border-red-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-colors"
                            >
                                <div>
                                    <div className="font-medium text-red-700">Delete All Candidates</div>
                                    {/* <div className="text-sm text-red-600">Remove all {candidatesLength} candidates</div> */}
                                </div>
                                <Trash2 className="w-5 h-5 text-red-600" />
                            </button>
                            
                            <button
                                onClick={handleDeleteRejected}
                                className="w-full flex items-center justify-between px-4 py-3 text-left border border-orange-200 rounded-lg hover:bg-orange-50 hover:border-orange-300 transition-colors"
                            >
                                <div>
                                    <div className="font-medium text-orange-700">Delete Rejected Candidates</div>
                                    {/* <div className="text-sm text-orange-600">Remove only rejected candidates</div> */}
                                </div>
                                <Trash2 className="w-5 h-5 text-orange-600" />
                            </button>
                        </div>
                    ) : (
                        // Confirmation View
                        <div className="mb-6">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                <div className="flex items-center">
                                    <Trash2 className="w-5 h-5 text-red-600 mr-2" />
                                    <span className="text-sm font-medium text-red-800">
                                        This action cannot be undone
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-center space-x-4">
                        <button
                            onClick={() => {
                                if (selectedAction) {
                                    setSelectedAction(null);
                                } else {
                                    onClose();
                                }
                            }}
                            disabled={isProcessing}
                            className="px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-200 border border-gray-300 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors disabled:opacity-50"
                        >
                            {selectedAction ? 'Back' : 'Cancel'}
                        </button>
                        
                        {selectedAction && (
                            <button
                                onClick={handleConfirm}
                                disabled={isProcessing}
                                className={`px-6 py-3 text-sm font-semibold text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                                    selectedAction === 'all' 
                                        ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                                        : 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                                }`}
                            >
                                {isProcessing ? (
                                    <>
                                        <Trash2 className="w-4 h-4 animate-spin" />
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <Trash2 className="w-4 h-4" />
                                        {selectedAction === 'all' ? 'Delete All' : 'Delete Rejected'}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteAllModal;